import { useState, useEffect, useMemo } from 'react';
import { toast } from 'react-hot-toast';
import { listFeedback, createFeedback, getFeedbackStats } from '../../../entities/feedback';

export function FeedbackPage() {
  const [activeTab, setActiveTab] = useState('feedback');

  // Form state
  const [formCategory, setFormCategory] = useState('');
  const [formTitle, setFormTitle] = useState('');
  const [formDesc, setFormDesc] = useState('');
  const [formPriority, setFormPriority] = useState('Medium');
  const [submitting, setSubmitting] = useState(false);

  // Tracker state
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const limit = 10;

  // Stats
  const [stats, setStats] = useState(null);

  const priorityColors = {
    High: 'bg-red-100 text-red-700',
    Medium: 'bg-yellow-100 text-yellow-700',
    Low: 'bg-gray-100 text-gray-700',
  };

  const statusColors = {
    Resolved: 'bg-green-100 text-green-700',
    'In Progress': 'bg-yellow-100 text-yellow-700',
    Open: 'bg-blue-100 text-blue-700',
  };

  const queryString = useMemo(() => {
    const p = new URLSearchParams();
    p.set('page', String(page));
    p.set('limit', String(limit));
    p.set('sortBy', 'created_at');
    p.set('sortDir', 'desc');
    return `?${p.toString()}`;
  }, [page]);

  const fetchIssues = async () => {
    setLoading(true);
    try {
      const res = await listFeedback(queryString);
      setIssues(res.data || []);
      setTotal(res.total || 0);
      setTotalPages(Math.ceil((res.total || 0) / limit));
    } catch {
      toast.error('Failed to load feedback');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const data = await getFeedbackStats();
      setStats(data);
    } catch {
      // silent
    }
  };

  useEffect(() => {
    if (activeTab === 'tracker') fetchIssues();
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, queryString]);

  const handleSubmitFeedback = async (e) => {
    e.preventDefault();
    if (!formCategory || !formTitle.trim()) {
      toast.error('Category and title are required');
      return;
    }
    setSubmitting(true);
    try {
      await createFeedback({
        category: formCategory,
        title: formTitle,
        description: formDesc,
        priority: formPriority,
      });
      toast.success('Feedback submitted');
      setFormCategory('');
      setFormTitle('');
      setFormDesc('');
      setFormPriority('Medium');
      fetchStats();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Failed to submit feedback');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <div className="p-6 w-full space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold">Feedback & Support</h2>
        <div className="flex gap-2 bg-gray-100 rounded-lg p-1">
          <button onClick={() => setActiveTab('feedback')} className={`px-4 py-1 rounded-md text-sm ${activeTab === 'feedback' ? 'bg-white shadow text-gray-800' : 'text-gray-500'}`}>
            Feedback
          </button>
          <button onClick={() => setActiveTab('tracker')} className={`px-4 py-1 rounded-md text-sm ${activeTab === 'tracker' ? 'bg-white shadow text-gray-800' : 'text-gray-500'}`}>
            Tracker
          </button>
        </div>
      </div>

      {/* Feedback Form Tab */}
      {activeTab === 'feedback' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 lg:col-span-2">
            <h3 className="text-lg font-medium mb-4">Submit Feedback</h3>
            <form onSubmit={handleSubmitFeedback} className="space-y-4">
              <select value={formCategory} onChange={(e) => setFormCategory(e.target.value)} className="w-full border border-gray-300 rounded-lg p-2 text-sm" required>
                <option value="">Select feedback type</option>
                <option value="Bug">Bug</option>
                <option value="Feature">Feature</option>
                <option value="Improvement">Improvement</option>
                <option value="General">General</option>
              </select>
              <input type="text" placeholder="Brief description of your feedback" value={formTitle} onChange={(e) => setFormTitle(e.target.value)} className="w-full border border-gray-300 rounded-lg p-2 text-sm" required />
              <textarea placeholder="Please provide detailed feedback..." rows="5" value={formDesc} onChange={(e) => setFormDesc(e.target.value)} className="w-full border border-gray-300 rounded-lg p-2 text-sm" />
              <div>
                <p className="text-sm font-medium mb-2">Priority</p>
                <div className="flex gap-4">
                  {['Low', 'Medium', 'High'].map(p => (
                    <label key={p} className="flex items-center gap-2 text-sm">
                      <input type="radio" name="priority" value={p} checked={formPriority === p} onChange={(e) => setFormPriority(e.target.value)} /> {p}
                    </label>
                  ))}
                </div>
              </div>
              <div className="flex gap-3">
                <button type="submit" disabled={submitting} className="flex-1 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50">
                  {submitting ? 'Sending...' : 'Send message'}
                </button>
                <button type="button" onClick={() => { setFormCategory(''); setFormTitle(''); setFormDesc(''); setFormPriority('Medium'); }} className="flex-1 border border-gray-300 rounded-lg py-2 hover:bg-gray-50">
                  Cancel
                </button>
              </div>
            </form>
          </div>

          <div className="space-y-4">
            <div className="bg-red-50 border border-red-200 rounded-xl p-4">
              <h4 className="font-medium text-red-700">24/7 Emergency Line</h4>
              <p className="text-red-600 font-semibold">(800) 555-HELP</p>
              <p className="text-xs text-red-500">For breakdowns, accidents or urgent load issues</p>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
              <h4 className="font-medium text-blue-700">Technical Support</h4>
              <p className="text-blue-600 font-semibold">(800) 555-TECH</p>
              <p className="text-sm text-blue-600">support@loadapp.com</p>
              <p className="text-xs text-gray-500">Mon-Fri 8AM-6PM EST</p>
            </div>
          </div>
        </div>
      )}

      {/* Tracker Tab */}
      {activeTab === 'tracker' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm lg:col-span-2 overflow-hidden">
            <h3 className="text-lg font-medium p-4 border-b">Feedback Tracker</h3>
            {loading ? (
              <div className="p-8 text-center text-sm text-gray-500">Loading...</div>
            ) : (
              <>
                <table className="w-full text-sm text-left">
                  <thead className="bg-gray-50 text-gray-600 font-medium">
                    <tr>
                      <th className="px-6 py-3">CATEGORY</th>
                      <th className="px-6 py-3">TITLE</th>
                      <th className="px-6 py-3">PRIORITY</th>
                      <th className="px-6 py-3">STATUS</th>
                      <th className="px-6 py-3">DATE</th>
                    </tr>
                  </thead>
                  <tbody>
                    {issues.length === 0 && (
                      <tr><td colSpan={5} className="px-6 py-8 text-center text-gray-500">No feedback found.</td></tr>
                    )}
                    {issues.map((i) => (
                      <tr key={i.id} className="border-t">
                        <td className="px-6 py-3">{i.category}</td>
                        <td className="px-6 py-3">
                          <p className="font-medium text-gray-800">{i.title}</p>
                          <p className="text-xs text-gray-500">{i.description?.substring(0, 40)}...</p>
                        </td>
                        <td className="px-6 py-3">
                          <span className={`px-2 py-1 text-xs rounded-full ${priorityColors[i.priority] || ''}`}>{i.priority}</span>
                        </td>
                        <td className="px-6 py-3">
                          <span className={`px-2 py-1 text-xs rounded-full ${statusColors[i.status] || ''}`}>{i.status}</span>
                        </td>
                        <td className="px-6 py-3">{formatDate(i.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="flex items-center justify-between px-6 py-3 bg-gray-50 border-t">
                  <span className="text-sm text-gray-500">
                    Showing <span className="font-medium">{issues.length === 0 ? 0 : (page - 1) * limit + 1}-{Math.min(page * limit, total)}</span> of {total}
                  </span>
                  <div className="flex items-center gap-1">
                    <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="px-2 py-1 text-sm rounded hover:bg-gray-200 disabled:opacity-50">&lt;</button>
                    <span className="px-3 py-1 text-sm rounded bg-blue-600 text-white">{page}</span>
                    <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} className="px-2 py-1 text-sm rounded hover:bg-gray-200 disabled:opacity-50">&gt;</button>
                  </div>
                </div>
              </>
            )}
          </div>

          <div className="space-y-4">
            <div className="bg-white border border-gray-200 rounded-xl p-4">
              <h4 className="font-medium mb-3">Feedback Statistics</h4>
              <p className="text-sm flex justify-between mb-1">
                <span>Open Issues</span> <span className="text-blue-600 font-medium">{stats?.open ?? '-'}</span>
              </p>
              <p className="text-sm flex justify-between mb-1">
                <span>In Progress</span> <span className="text-yellow-600 font-medium">{stats?.inProgress ?? '-'}</span>
              </p>
              <p className="text-sm flex justify-between mb-1">
                <span>Resolved</span> <span className="text-green-600 font-medium">{stats?.resolved ?? '-'}</span>
              </p>
              <p className="text-sm flex justify-between">
                <span>Total</span> <span className="font-medium">{stats?.total ?? '-'}</span>
              </p>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-2">
              <h4 className="font-medium mb-2">Quick Links</h4>
              <button className="w-full text-left border rounded-lg px-3 py-2 hover:bg-gray-50 text-sm">User Guide</button>
              <button className="w-full text-left border rounded-lg px-3 py-2 hover:bg-gray-50 text-sm">Community Forum</button>
              <button className="w-full text-left border rounded-lg px-3 py-2 hover:bg-gray-50 text-sm">Report Security Issue</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
